// Casio FX-991ES Plus — calculation engine.
// Translates the calculator's "natural" input syntax into mathjs syntax,
// evaluates with current angle mode, and exposes helpers for EQN / TABLE modes.

import { create, all, type MathJsInstance } from "mathjs";

const math: MathJsInstance = create(all, {});

export type AngleMode = "DEG" | "RAD" | "GRAD";
export type DisplayFormat = "NORM" | "FIX" | "SCI";

export interface EvalOptions {
  angle: AngleMode;
  ans: number;
  vars: Record<string, number>;
}

export interface EvalResult {
  ok: true;
  value: number;
  display: string; // formatted decimal string
  fractionTex?: string; // optional stacked fraction representation
  isExact?: boolean;
}
export interface EvalError {
  ok: false;
  kind: "Math ERROR" | "Syntax ERROR" | "Stack ERROR" | "Argument ERROR";
  message: string;
}

// ---------------------------------------------------------------------------
// Translation: Casio display syntax  ->  mathjs syntax
// ---------------------------------------------------------------------------
// Casio characters used in the buffer:
//   × ÷ − π √ ³√  (cube root)  ²(x²)  ⁻¹(x⁻¹)  ! E   °'"
//   10^( e^( log( ln( sin( cos( tan(  asin( acos( atan(
//   Frac(  ( a/b → "a/b" )  Ans  RanInt#

const REPLACEMENTS: Array<[RegExp, string]> = [
  [/×10\^?/g, "*10^"],
  [/×/g, "*"],
  [/÷/g, "/"],
  [/−/g, "-"],
  [/π/g, "pi"],
  [/Ans/g, "(ans)"],
  [/³√/g, "cbrt"],
  [/√/g, "sqrt"],
  [/(\d)!/g, "$1!"], // factorial — mathjs supports !
];

function translate(input: string, opts: EvalOptions): string {
  let s = input;
  for (const [re, rep] of REPLACEMENTS) s = s.replace(re, rep);
  // Implicit multiplication: 2pi -> 2*pi, 2( -> 2*(, )( -> )*(
  s = s.replace(/(\d|\))(\(|pi|e\b|sqrt|cbrt|sin|cos|tan|asin|acos|atan|log|ln|exp)/g, "$1*$2");
  // Squared / power glyphs already typed by buttons map to ^2 / ^( ) at insertion time.
  // ln(x) -> log(x); log(x) -> log10(x) in mathjs
  s = s.replace(/\bln\(/g, "log(");
  s = s.replace(/\blog\(/g, "log10(");
  // Insert ANS where written
  s = s.replace(/\(ans\)/g, `(${opts.ans})`);
  // Variables A,B,C,D,X,Y,M
  for (const [k, v] of Object.entries(opts.vars)) {
    s = s.replace(new RegExp(`\\b${k}\\b`, "g"), `(${v})`);
  }
  return s;
}

function wrapAngle(s: string, mode: AngleMode): string {
  if (mode === "RAD") return s;
  // For DEG / GRAD, convert arguments of trig functions and the inverse.
  // Replace sin(x) -> sin((x)*pi/180) etc.
  const factor = mode === "DEG" ? "pi/180" : "pi/200";
  const inv = mode === "DEG" ? "180/pi" : "200/pi";
  // forward trig
  s = s.replace(/\b(sin|cos|tan)\(/g, `$1((${factor})*(`);
  // close extra paren — naive: every replacement opened one extra "(", we add ")" at end of matching paren
  s = balanceWrapped(s, /\b(sin|cos|tan)\(\((?:pi\/180|pi\/200)\)\*\(/g);
  // inverse trig: result is in radians, convert to degrees/grads
  s = s.replace(/\b(asin|acos|atan)\(/g, `(${inv})*(($1(`);
  s = balanceWrapped(s, /\(\((?:180\/pi|200\/pi)\)\*\(\((?:asin|acos|atan)\(/g);
  return s;
}

// helper: each match opened one extra inner "(", ensure matching ")"
function balanceWrapped(s: string, _re: RegExp): string {
  // Count unbalanced parens left-to-right and append ")"s at end.
  let depth = 0;
  for (const ch of s) {
    if (ch === "(") depth++;
    else if (ch === ")") depth--;
  }
  while (depth-- > 0) s += ")";
  return s;
}

// ---------------------------------------------------------------------------
// Public evaluation
// ---------------------------------------------------------------------------
export function evaluate(input: string, opts: EvalOptions): EvalResult | EvalError {
  if (!input.trim()) {
    return { ok: false, kind: "Syntax ERROR", message: "Empty expression" };
  }
  let translated: string;
  try {
    translated = translate(input, opts);
    translated = wrapAngle(translated, opts.angle);
  } catch (e) {
    return { ok: false, kind: "Syntax ERROR", message: String((e as Error).message) };
  }
  let raw: unknown;
  try {
    raw = math.evaluate(translated);
  } catch (e) {
    const msg = String((e as Error).message);
    if (/parenthes|unexpected|undefined/i.test(msg)) {
      return { ok: false, kind: "Syntax ERROR", message: msg };
    }
    return { ok: false, kind: "Math ERROR", message: msg };
  }
  const value = Number(raw);
  if (!Number.isFinite(value)) {
    return { ok: false, kind: "Math ERROR", message: "Result is not a finite number" };
  }
  const display = formatNumber(value);
  const frac = toFractionTex(value);
  return { ok: true, value, display, fractionTex: frac, isExact: !!frac };
}

// ---------------------------------------------------------------------------
// Number formatting (NORM mode)
// ---------------------------------------------------------------------------
export function formatNumber(n: number, fmt: DisplayFormat = "NORM", digits = 10): string {
  if (!Number.isFinite(n)) return "Math ERROR";
  if (Math.abs(n) < 1e-12) n = 0;
  if (fmt === "FIX") return n.toFixed(digits);
  if (fmt === "SCI") return n.toExponential(digits).replace("e", "×10^");
  // NORM: behave like Casio NORM 1 — show scientific outside 1e-2..1e10
  const abs = Math.abs(n);
  if (abs !== 0 && (abs < 1e-2 || abs >= 1e10)) {
    return n.toExponential(9).replace(/0+e/, "e").replace("e", "×10^");
  }
  // round to 10 significant digits, strip trailing zeros
  const s = n.toPrecision(10);
  return parseFloat(s).toString();
}

// Try to express number as a small fraction p/q (q ≤ 1000, numerator ≤ 10⁶)
export function toFractionTex(n: number): string | undefined {
  if (!Number.isFinite(n) || Number.isInteger(n)) return undefined;
  const sign = n < 0 ? "-" : "";
  const x = Math.abs(n);
  // continued-fraction approximation
  let h1 = 1, h0 = 0, k1 = 0, k0 = 1;
  let b = x;
  for (let i = 0; i < 30; i++) {
    const a = Math.floor(b);
    const h2 = a * h1 + h0;
    const k2 = a * k1 + k0;
    if (k2 > 1000) break;
    h0 = h1; h1 = h2; k0 = k1; k1 = k2;
    if (Math.abs(x - h1 / k1) < 1e-10) {
      if (k1 === 1) return undefined;
      // proper / mixed display
      const whole = Math.trunc(h1 / k1);
      const num = h1 % k1;
      if (whole !== 0 && num !== 0) {
        return `${sign}${whole}\\,\\frac{${num}}{${k1}}`;
      }
      return `${sign}\\frac{${h1}}{${k1}}`;
    }
    b = 1 / (b - a);
    if (!Number.isFinite(b)) break;
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// EQN mode — polynomial solver
// ---------------------------------------------------------------------------
export type EqnKind = "lin2" | "lin3" | "quad" | "cubic";

export interface EqnSolution {
  ok: true;
  roots: { re: number; im: number }[];
  pretty: string[];
}
export interface EqnFailure { ok: false; message: string; }

export function solveQuadratic(a: number, b: number, c: number): EqnSolution | EqnFailure {
  if (a === 0) return { ok: false, message: "a must be non-zero" };
  const disc = b * b - 4 * a * c;
  if (disc >= 0) {
    const sq = Math.sqrt(disc);
    const x1 = (-b + sq) / (2 * a);
    const x2 = (-b - sq) / (2 * a);
    return {
      ok: true,
      roots: [{ re: x1, im: 0 }, { re: x2, im: 0 }],
      pretty: [`x_{1} = ${formatNumber(x1)}`, `x_{2} = ${formatNumber(x2)}`],
    };
  }
  const re = -b / (2 * a);
  const im = Math.sqrt(-disc) / (2 * a);
  return {
    ok: true,
    roots: [{ re, im }, { re, im: -im }],
    pretty: [
      `x_{1} = ${formatNumber(re)} + ${formatNumber(im)}i`,
      `x_{2} = ${formatNumber(re)} − ${formatNumber(im)}i`,
    ],
  };
}

export function solveCubic(a: number, b: number, c: number, d: number): EqnSolution | EqnFailure {
  if (a === 0) return solveQuadratic(b, c, d);
  // Normalise: x³ + px² + qx + r
  const p = b / a, q = c / a, r = d / a;
  // Depressed cubic via x = y - p/3
  const A = q - (p * p) / 3;
  const B = (2 * p ** 3) / 27 - (p * q) / 3 + r;
  const disc = (B * B) / 4 + (A ** 3) / 27;
  const roots: { re: number; im: number }[] = [];
  if (disc > 0) {
    const sq = Math.sqrt(disc);
    const u = Math.cbrt(-B / 2 + sq);
    const v = Math.cbrt(-B / 2 - sq);
    const y1 = u + v;
    roots.push({ re: y1 - p / 3, im: 0 });
    const re = -(u + v) / 2 - p / 3;
    const im = (Math.sqrt(3) / 2) * (u - v);
    roots.push({ re, im });
    roots.push({ re, im: -im });
  } else if (disc === 0) {
    const u = Math.cbrt(-B / 2);
    roots.push({ re: 2 * u - p / 3, im: 0 });
    roots.push({ re: -u - p / 3, im: 0 });
    roots.push({ re: -u - p / 3, im: 0 });
  } else {
    const rho = Math.sqrt(-(A ** 3) / 27);
    const theta = Math.acos(-B / (2 * rho));
    const m = 2 * Math.cbrt(rho);
    for (let k = 0; k < 3; k++) {
      const y = m * Math.cos((theta + 2 * Math.PI * k) / 3);
      roots.push({ re: y - p / 3, im: 0 });
    }
  }
  return {
    ok: true,
    roots,
    pretty: roots.map((r, i) =>
      r.im === 0
        ? `x_{${i + 1}} = ${formatNumber(r.re)}`
        : `x_{${i + 1}} = ${formatNumber(r.re)} ${r.im > 0 ? "+" : "−"} ${formatNumber(Math.abs(r.im))}i`,
    ),
  };
}

// 2x2 linear system: a1 x + b1 y = c1 ; a2 x + b2 y = c2
export function solveLin2(a1: number, b1: number, c1: number, a2: number, b2: number, c2: number): EqnSolution | EqnFailure {
  const det = a1 * b2 - a2 * b1;
  if (det === 0) return { ok: false, message: "No unique solution" };
  const x = (c1 * b2 - c2 * b1) / det;
  const y = (a1 * c2 - a2 * c1) / det;
  return { ok: true, roots: [{ re: x, im: 0 }, { re: y, im: 0 }], pretty: [`x = ${formatNumber(x)}`, `y = ${formatNumber(y)}`] };
}

// 3x3 linear system via Cramer
export function solveLin3(m: number[][]): EqnSolution | EqnFailure {
  // m is 3x4 augmented
  const det3 = (a: number[][]) =>
    a[0][0] * (a[1][1] * a[2][2] - a[1][2] * a[2][1]) -
    a[0][1] * (a[1][0] * a[2][2] - a[1][2] * a[2][0]) +
    a[0][2] * (a[1][0] * a[2][1] - a[1][1] * a[2][0]);
  const A = m.map((r) => r.slice(0, 3));
  const D = det3(A);
  if (D === 0) return { ok: false, message: "No unique solution" };
  const sub = (col: number) => m.map((r) => r.slice(0, 3).map((v, i) => (i === col ? r[3] : v)));
  const x = det3(sub(0)) / D, y = det3(sub(1)) / D, z = det3(sub(2)) / D;
  return {
    ok: true,
    roots: [{ re: x, im: 0 }, { re: y, im: 0 }, { re: z, im: 0 }],
    pretty: [`x = ${formatNumber(x)}`, `y = ${formatNumber(y)}`, `z = ${formatNumber(z)}`],
  };
}

// ---------------------------------------------------------------------------
// TABLE mode — generate (x, f(x)) for a function expression
// ---------------------------------------------------------------------------
export function generateTable(
  expr: string,
  start: number, end: number, step: number,
  angle: AngleMode = "RAD",
): { x: number; y: number | null }[] {
  const rows: { x: number; y: number | null }[] = [];
  const n = Math.min(30, Math.floor((end - start) / step) + 1);
  for (let i = 0; i < n; i++) {
    const x = start + i * step;
    const r = evaluate(expr.replace(/X/g, `(${x})`), { angle, ans: 0, vars: {} });
    rows.push({ x, y: r.ok ? r.value : null });
  }
  return rows;
}
